'use client';

import PulsePagination from '@/components/ui/PulsePagination';
import { Checkbox } from '@/components/shadcn/checkbox';
import { cn } from '@/utils/cn';
import {
  ChevronDownIcon,
  ChevronsUpDownIcon,
  ChevronUpIcon
} from 'lucide-react';
import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';

type Align = 'left' | 'center' | 'right';
type Breakpoint = 'sm' | 'md' | 'lg' | 'xl';

export type DataTableColumn<Row> = {
  /** Stable identifier for the column (used as the React key). */
  key: string;
  header: React.ReactNode;
  /** Visually hide the header text but keep it for screen readers (e.g. an actions column). */
  srOnlyHeader?: boolean;
  cell: (row: Row) => React.ReactNode;
  align?: Align;
  /**
   * The cell contains its own controls (menus, links, toggles); clicks inside
   * it do not trigger the row's onRowClick.
   */
  interactive?: boolean;
  /** Hide the column on viewports narrower than this breakpoint. */
  hideBelow?: Breakpoint;
  /**
   * The column takes the table's leftover width and clips content that does
   * not fit with an ellipsis, so one long value cannot widen the table into a
   * horizontal scroll. At most one column per table should set this.
   */
  truncate?: boolean;
  /** Render a sort control in the header. */
  sortable?: boolean;
  /**
   * Value to sort rows by when the table sorts client-side (no onSortChange).
   * Required for `sortable` columns in that mode; nulls sort last.
   */
  sortValue?: (row: Row) => number | string | null | undefined;
  /** Direction the first click sorts in (default 'asc'; use 'desc' for counts). */
  firstSortDirection?: SortDirection;
  headerClassName?: string;
  cellClassName?: string;
};

type SortDirection = 'asc' | 'desc';

export type DataTableSort = {
  key: string;
  direction: SortDirection;
};

export type RowKey = string | number;

export type DataTablePagination = {
  page: number;
  pageSize: number;
  totalCount: number;
};

const alignClasses: Record<Align, string | undefined> = {
  left: undefined,
  center: 'text-center',
  right: 'text-right'
};

// Tailwind needs literal class names, so each breakpoint is spelled out.
const hideBelowClasses: Record<Breakpoint, string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell'
};

// A cell that is both full-width and zero-max-width absorbs the width the
// other columns leave over, and gives its content a width to be clipped to.
// The min-width keeps it readable rather than letting the columns that size to
// their own content squeeze it away; below that the table scrolls as before.
const truncateCellClasses = 'w-full min-w-[12rem] max-w-0';

export function TableEmptyState({
  title,
  description,
  action
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border py-16 text-center">
      <p className="text-sm text-muted-foreground">{title}</p>
      {description && (
        <p className="mt-1 text-sm text-muted-foreground">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

type SortKey = number | string | null | undefined;

/** Orders two sort keys. A row with no value sorts last in either direction. */
function compareSortKeys(a: SortKey, b: SortKey, direction: 1 | -1): number {
  if (a == null || b == null) {
    if (a == null && b == null) return 0;
    return a == null ? 1 : -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return (a - b) * direction;
  }
  return (
    String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: 'base'
    }) * direction
  );
}

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  dense = false,
  loading,
  skeletonRows = 5,
  empty,
  onRowClick,
  rowClassName,
  sort,
  onSortChange,
  pagination,
  className,
  enableSelection = false,
  selectedKeys,
  onSelectionChange,
  selectableRow
}: {
  columns: DataTableColumn<Row>[];
  /** Row data; `undefined`/`null` (not yet loaded) renders the loading skeleton. */
  rows: Row[] | null | undefined;
  rowKey: (row: Row) => string | number;
  dense?: boolean;
  /** Overrides the default loading detection (`rows == null`). */
  loading?: boolean;
  skeletonRows?: number;
  /**
   * Rendered instead of the table when there are no rows — typically a
   * `TableEmptyState`. Defaults to a plain "No results." empty state.
   */
  empty?: React.ReactNode;
  onRowClick?: (row: Row) => void;
  rowClassName?: (row: Row) => string | undefined;
  /** Current sort, when the caller owns sorting (e.g. a server-sorted table). */
  sort?: DataTableSort | null;
  /**
   * Called when a sortable header is clicked (asc → desc → null). When omitted,
   * the table sorts client-side using each column's sortValue.
   */
  onSortChange?: (sort: DataTableSort | null) => void;
  /** When set, renders PulsePagination below the table if there is more than one page. */
  pagination?: DataTablePagination;
  /** Applied to the bordered card around the table. */
  className?: string;
  /** Adds a leading checkbox column. Selection itself is owned by the caller. */
  enableSelection?: boolean;
  selectedKeys?: Set<RowKey>;
  onSelectionChange?: (keys: Set<RowKey>) => void;
  /**
   * Rows a caller can act on. A row this excludes shows no checkbox and is
   * left out of the header's select-all, so a selection can't be built that
   * has nothing to do with it.
   */
  selectableRow?: (row: Row) => boolean;
}) {
  const isLoading = loading ?? rows == null;
  const [internalSort, setInternalSort] = useState<DataTableSort | null>(null);
  const activeSort = onSortChange ? (sort ?? null) : internalSort;

  const cycleSort = (col: DataTableColumn<Row>) => {
    const first = col.firstSortDirection ?? 'asc';
    const second = first === 'asc' ? 'desc' : 'asc';
    let next: DataTableSort | null;
    if (activeSort?.key !== col.key) {
      next = { key: col.key, direction: first };
    } else if (activeSort.direction === first) {
      next = { key: col.key, direction: second };
    } else {
      next = null;
    }
    if (onSortChange) {
      onSortChange(next);
    } else {
      setInternalSort(next);
    }
  };

  // Client-side sorting, for tables that hold every row they can show. A table
  // that owns its sort (onSortChange) has already ordered what it passed in.
  let displayRows = rows;
  const sortColumn =
    activeSort && !onSortChange
      ? columns.find((col) => col.key === activeSort.key)
      : undefined;
  if (rows && activeSort && sortColumn?.sortValue) {
    const sortValue = sortColumn.sortValue;
    const direction = activeSort.direction === 'asc' ? 1 : -1;
    // Each row's value is read once rather than on every comparison.
    displayRows = rows
      .map((row) => ({ row, key: sortValue(row) }))
      .sort((a, b) => compareSortKeys(a.key, b.key, direction))
      .map((entry) => entry.row);
  }

  // The empty state replaces the table entirely — an empty table sizes its
  // columns from the header text alone, which bunches the right-hand headers
  // together over a blank body.
  if (!isLoading && (!rows || rows.length === 0)) {
    return <>{empty ?? <TableEmptyState title="No results." />}</>;
  }

  const cellPadding = dense ? 'py-2.5' : 'py-4';

  const selected = selectedKeys ?? new Set<RowKey>();
  const canSelect = (row: Row) => selectableRow?.(row) ?? true;
  const selectableRows = (displayRows ?? []).filter(canSelect);
  const selectedOnPage = selectableRows.filter((row) =>
    selected.has(rowKey(row))
  ).length;

  const toggleRow = (row: Row) => {
    const next = new Set(selected);
    const key = rowKey(row);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange?.(next);
  };

  // Select-all covers this page only, which is all the caller can see and the
  // only set it can describe honestly in a confirmation.
  const toggleAll = () => {
    const next = new Set(selected);
    const everySelected = selectedOnPage === selectableRows.length;
    for (const row of selectableRows) {
      if (everySelected) next.delete(rowKey(row));
      else next.add(rowKey(row));
    }
    onSelectionChange?.(next);
  };

  const selectionColumn: DataTableColumn<Row> = {
    key: '__selection',
    srOnlyHeader: false,
    headerClassName: 'w-10',
    cellClassName: 'w-10',
    interactive: true,
    header: (
      <Checkbox
        aria-label="Select all rows on this page"
        disabled={selectableRows.length === 0}
        checked={
          selectedOnPage === 0
            ? false
            : selectedOnPage === selectableRows.length
              ? true
              : 'indeterminate'
        }
        onCheckedChange={toggleAll}
      />
    ),
    cell: (row) =>
      canSelect(row) ? (
        <Checkbox
          aria-label="Select row"
          checked={selected.has(rowKey(row))}
          onCheckedChange={() => toggleRow(row)}
        />
      ) : null
  };

  const allColumns = enableSelection ? [selectionColumn, ...columns] : columns;

  const bodyRows = isLoading
    ? Array.from({ length: skeletonRows }, (_, i) => (
        <tr key={i} className="border-t border-border">
          {allColumns.map((col) => (
            <td
              key={col.key}
              className={cn(
                'px-4',
                cellPadding,
                col.hideBelow && hideBelowClasses[col.hideBelow],
                col.truncate && truncateCellClasses
              )}
            >
              <Skeleton />
            </td>
          ))}
        </tr>
      ))
    : displayRows!.map((row) => (
        <tr
          key={rowKey(row)}
          onClick={onRowClick ? () => onRowClick(row) : undefined}
          className={cn(
            'border-t border-border',
            onRowClick && 'cursor-pointer hover:bg-muted',
            enableSelection && selected.has(rowKey(row)) && 'bg-muted/40',
            rowClassName?.(row)
          )}
        >
          {allColumns.map((col) => (
            <td
              key={col.key}
              onClick={
                col.interactive && onRowClick
                  ? (e) => e.stopPropagation()
                  : undefined
              }
              className={cn(
                'px-4',
                cellPadding,
                col.align && alignClasses[col.align],
                col.hideBelow && hideBelowClasses[col.hideBelow],
                col.truncate && truncateCellClasses,
                col.cellClassName
              )}
            >
              {col.truncate ? (
                <div className="truncate">{col.cell(row)}</div>
              ) : (
                col.cell(row)
              )}
            </td>
          ))}
        </tr>
      ));

  const totalPages = pagination
    ? Math.ceil(pagination.totalCount / pagination.pageSize)
    : 0;

  return (
    <>
      <div
        className={cn(
          'overflow-hidden rounded-lg border border-border',
          className
        )}
      >
        <div className="overflow-x-auto whitespace-nowrap">
          <table className="w-full text-left text-sm text-foreground">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                {allColumns.map((col) => {
                  const sorted =
                    activeSort?.key === col.key ? activeSort.direction : null;
                  const label = col.srOnlyHeader ? (
                    <span className="sr-only">{col.header}</span>
                  ) : (
                    col.header
                  );
                  return (
                    <th
                      key={col.key}
                      aria-sort={
                        col.sortable
                          ? sorted === 'asc'
                            ? 'ascending'
                            : sorted === 'desc'
                              ? 'descending'
                              : 'none'
                          : undefined
                      }
                      className={cn(
                        'px-4 py-2.5 font-semibold',
                        col.align && alignClasses[col.align],
                        col.hideBelow && hideBelowClasses[col.hideBelow],
                        col.truncate && truncateCellClasses,
                        col.headerClassName
                      )}
                    >
                      {col.sortable ? (
                        <button
                          type="button"
                          onClick={() => cycleSort(col)}
                          className={cn(
                            'inline-flex items-center gap-1 uppercase hover:text-foreground',
                            sorted && 'text-foreground'
                          )}
                        >
                          {label}
                          {sorted === 'asc' ? (
                            <ChevronUpIcon size={14} />
                          ) : sorted === 'desc' ? (
                            <ChevronDownIcon size={14} />
                          ) : (
                            <ChevronsUpDownIcon
                              size={14}
                              className="opacity-50"
                            />
                          )}
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>{bodyRows}</tbody>
          </table>
        </div>
      </div>
      {pagination && totalPages > 1 && (
        <div className="mt-4">
          <PulsePagination
            page={pagination.page}
            totalPages={totalPages}
            pageSize={pagination.pageSize}
          />
        </div>
      )}
    </>
  );
}
